import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  BookOpen,
  CreditCard,
  Settings,
  BarChart3,
  TrendingUp,
  Shield,
  UserPlus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronRight,
  Activity,
  Database,
  Server,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_users: 0,
    total_questions: 878,
    total_attempts: 0,
    total_revenue: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [systemStatus, setSystemStatus] = useState({
    database: "healthy",
    ai_service: "healthy",
    payments: "healthy",
  });
  const [loading, setLoading] = useState(true);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      // Fetch schema info to verify external schemas are loaded
      const schemaRes = await axios.get(`${API}/schemas/info`);
      
      // Mock admin data for now
      setStats({
        total_users: 156,
        total_questions: 878,
        total_attempts: 2450,
        total_revenue: 1250,
      });

      setRecentUsers([
        { user_id: "user_001", name: "Dr. Aman Kumar", email: "aman@example.com", role: "student", credits: 45, joined: "2 days ago" },
        { user_id: "user_002", name: "Dr. Priya Sharma", email: "priya@example.com", role: "sme", credits: 100, joined: "1 week ago" },
        { user_id: "user_003", name: "Rahul Verma", email: "rahul@example.com", role: "student", credits: 12, joined: "2 weeks ago" },
        { user_id: "user_004", name: "Dr. Sneha Patel", email: "sneha@example.com", role: "student", credits: 78, joined: "3 weeks ago" },
      ]);

      // System status based on schema loading
      setSystemStatus({
        database: "healthy",
        ai_service: "healthy",
        payments: "healthy",
        schemas: schemaRes.data.schemas_loaded?.length === 5 ? "healthy" : "warning",
      });
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin dashboard");
    } finally {
      setLoading(false);
    }
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const updateUserRole = async (newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.user_id}/role`,
        { role: newRole },
        {  }
      );
      toast.success(`User role updated to ${newRole}`);
      setUserModalOpen(false);
      fetchAdminData();
    } catch (error) {
      toast.error("Failed to update user role");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy": return "text-emerald-600 bg-emerald-100";
      case "warning": return "text-amber-600 bg-amber-100";
      case "error": return "text-red-600 bg-red-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-700";
      case "sme": return "bg-purple-100 text-purple-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#2E3A59] font-medium">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="admin" />
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-[#2E3A59]">
                Admin Dashboard
              </h1>
              <p className="text-slate-500 mt-1">
                Manage users, questions, and system settings
              </p>
            </div>
            <Button
              onClick={fetchAdminData}
              variant="outline"
              className="shrink-0"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#2E3A59]">{stats.total_users}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Total Users</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#2E3A59]">{stats.total_questions}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Questions</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#2E3A59]">{stats.total_attempts}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Attempts</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#FDF8E6] rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-2xl lg:text-3xl font-bold text-[#C9A227]">${stats.total_revenue}</p>
                  <p className="text-xs lg:text-sm text-slate-500">Revenue</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Users Table */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h2 className="font-semibold text-[#2E3A59] flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Recent Users
                  </h2>
                  
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <Button variant="outline" size="sm">
                      <Filter className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Users List */}
                <div className="space-y-3">
                  {recentUsers.map((u, i) => (
                    <div
                      key={u.user_id || i}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => openUserModal(u)}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#2E3A59] flex items-center justify-center text-white font-bold">
                        {u.name?.charAt(0) || "U"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#2E3A59] truncate">{u.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeColor(u.role)}`}>
                            {u.role}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 truncate">{u.email}</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-semibold text-[#C9A227]">{u.credits} credits</p>
                        <p className="text-xs text-slate-400">{u.joined}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => navigate("/admin/users")}
                >
                  View All Users
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* System Status */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-600" />
                  System Status
                </h3>
                <div className="space-y-3">
                  {Object.entries(systemStatus).map(([service, status]) => (
                    <div key={service} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {status === "healthy" ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        )}
                        <span className="text-sm capitalize text-slate-700">{service.replace("_", " ")}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate("/admin/users")}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Manage Users
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate("/admin/questions")}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Manage Questions
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate("/admin/settings")}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                </div>
              </div>

              {/* Database Stats */}
              <div className="bg-white rounded-xl border border-slate-100 p-4 lg:p-6">
                <h3 className="font-semibold text-[#2E3A59] mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-purple-600" />
                  Database
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Questions</span>
                    <span className="text-sm font-semibold text-[#2E3A59]">878</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">External Schemas</span>
                    <span className="text-sm font-semibold text-emerald-600">5 loaded</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Users</span>
                    <span className="text-sm font-semibold text-[#2E3A59]">{stats.total_users}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Attempts</span>
                    <span className="text-sm font-semibold text-[#2E3A59]">{stats.total_attempts}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* User Modal */}
      <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#2E3A59] flex items-center justify-center text-white text-2xl font-bold">
                  {selectedUser.name?.charAt(0) || "U"}
                </div>
                <div>
                  <h3 className="font-semibold text-[#2E3A59]">{selectedUser.name}</h3>
                  <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${getRoleBadgeColor(selectedUser.role)}`}>
                    {selectedUser.role}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 p-4 bg-slate-50 rounded-xl">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Credits</span>
                  <span className="text-sm font-semibold text-[#C9A227]">{selectedUser.credits}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Joined</span>
                  <span className="text-sm text-[#2E3A59]">{selectedUser.joined}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">User ID</span>
                  <span className="text-xs font-mono text-slate-400">{selectedUser.user_id}</span>
                </div>
              </div>

              {/* Change Role */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Change Role</p>
                <div className="flex gap-2">
                  <Button
                    variant={selectedUser.role === "student" ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateUserRole("student")}
                    className={selectedUser.role === "student" ? "bg-blue-600" : ""}
                  >
                    Student
                  </Button>
                  <Button
                    variant={selectedUser.role === "sme" ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateUserRole("sme")}
                    className={selectedUser.role === "sme" ? "bg-purple-600" : ""}
                  >
                    SME
                  </Button>
                  <Button
                    variant={selectedUser.role === "admin" ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateUserRole("admin")}
                    className={selectedUser.role === "admin" ? "bg-red-600" : ""}
                  >
                    Admin
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" className="flex-1">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" className="flex-1 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
